import Icon from "@components/Atoms/Icon";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import type UserInfo from "@ext/security/logic/User/UserInfo";
import {
	Avatar,
	AvatarFallback,
	AvatarLabel,
	AvatarLabelAvatar,
	AvatarLabelDescription,
	AvatarLabelTitle,
	getAvatarFallback,
} from "@ui-kit/Avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuTriggerButton,
} from "@ui-kit/Dropdown";
import { GlassToolbarToggleButton, type GlassToolbarToggleButtonProps } from "@ui-kit/GlassToolbar";
import { DisabledReason } from "@ui-kit/Tooltip";
import { forwardRef, type ReactNode } from "react";

export const LogoutButton = ({ authUrl }: { authUrl: string }) => {
	return (
		<a className="flex items-center gap-2 w-full" data-qa="qa-clickable" href={authUrl}>
			<SignOut />
		</a>
	);
};

export const SignOut = () => {
	return (
		<>
			<Icon code="log-out" />
			{t("sing-out")}
		</>
	);
};

export const OpenSettings = ({ title }: { title?: string }) => {
	return (
		<>
			<Icon code="settings" />
			{title ?? t("app-settings.title")}
		</>
	);
};

export const OpenOrganizationSettings = () => {
	return (
		<>
			<Icon code="building" />
			{t("enterprise-cloud.org-settings.title")}
		</>
	);
};

export type UserAvatarTriggerVariant = "default" | "glass";

export const GlassAvatarToggleButton = forwardRef<HTMLButtonElement, GlassToolbarToggleButtonProps>(
	({ className, ...props }, ref) => (
		<GlassToolbarToggleButton
			focusable
			ref={ref}
			{...props}
			className={cn(
				"relative size-10 !p-0 after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:content-[''] hover:after:bg-alpha-high-90 data-[state=on]:after:bg-alpha-high-80 aria-expanded:after:bg-alpha-high-80",
				className,
			)}
		/>
	),
);

interface UserAvatarProps {
	logoutComponent: React.ReactNode;
	onLogoutClick?: () => void;
	settingsComponent?: React.ReactNode;
	onSettingsClick?: () => void;
	organizationSettingsComponent?: React.ReactNode;
	onOrganizationSettingsClick?: () => void;
	organizationSettingsDisabled?: boolean;
	organizationSettingsDisabledReason?: ReactNode;
	homeLayoutControlsComponent?: React.ReactNode;
	themeToggleComponent?: React.ReactNode;
	triggerVariant?: UserAvatarTriggerVariant;
}

const getCode = (user: UserInfo): string => {
	let code = getAvatarFallback(user?.name ?? "");
	if (!code) code = user?.mail?.[0] ?? "";
	if (!code) code = "";
	return code.toUpperCase();
};

export const UserAvatar = ({
	logoutComponent,
	onLogoutClick,
	settingsComponent,
	onSettingsClick,
	organizationSettingsComponent,
	onOrganizationSettingsClick,
	organizationSettingsDisabled,
	organizationSettingsDisabledReason,
	homeLayoutControlsComponent,
	themeToggleComponent,
	triggerVariant = "default",
}: UserAvatarProps) => {
	const userInfo = PageDataContextService.value?.user?.info;
	const code = getCode(userInfo);
	const avatar = (
		<Avatar size={triggerVariant === "glass" ? "lg" : "sm"}>
			<AvatarFallback uniqueId={userInfo?.mail ?? ""}>{code}</AvatarFallback>
		</Avatar>
	);

	return (
		<DropdownMenu>
			{triggerVariant === "glass" ? (
				<DropdownMenuTrigger asChild>
					<GlassAvatarToggleButton aria-label={t("account")}>{avatar}</GlassAvatarToggleButton>
				</DropdownMenuTrigger>
			) : (
				<DropdownMenuTriggerButton className="aspect-square rounded-full p-0" variant="ghost">
					{avatar}
				</DropdownMenuTriggerButton>
			)}
			<DropdownMenuContent align="end">
				<DropdownMenuGroup>
					<DropdownMenuItem className="pointer-events-none">
						<AvatarLabel size="md">
							<AvatarLabelAvatar>
								<AvatarFallback>{code}</AvatarFallback>
							</AvatarLabelAvatar>
							<AvatarLabelTitle>{userInfo?.name ?? ""}</AvatarLabelTitle>
							<AvatarLabelDescription>{userInfo?.mail ?? ""}</AvatarLabelDescription>
						</AvatarLabel>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					{homeLayoutControlsComponent}
					{themeToggleComponent}
					{organizationSettingsComponent &&
						(organizationSettingsDisabledReason ? (
							<DisabledReason reason={organizationSettingsDisabledReason}>
								<DropdownMenuItem>{organizationSettingsComponent}</DropdownMenuItem>
							</DisabledReason>
						) : (
							<DropdownMenuItem
								disabled={organizationSettingsDisabled}
								onSelect={onOrganizationSettingsClick}
							>
								{organizationSettingsComponent}
							</DropdownMenuItem>
						))}
					{settingsComponent && (
						<DropdownMenuItem onSelect={onSettingsClick}>{settingsComponent}</DropdownMenuItem>
					)}
					<DropdownMenuItem onSelect={onLogoutClick}>{logoutComponent}</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
